import { IdentityUserAri, MeasurementSiteUserAri, PlatformSiteAri } from '@atlassian/ari';
import { type HierarchicalLogger } from '@atlassian/logger-interface';
import { type Metrics } from '@atlassian/metrics-interface';
import type { CachedTapClient } from '@post-office/cache-clients/clients/tap-client';
import type {
	TapProvider as SharedContractsTapProvider,
	TapTraitsRequestConfig,
} from '@post-office/shared-contracts/tap-provider';
import type { Entity } from '@post-office/tap-client';
import {
	getSiteUserTraitsHelper,
	type SiteUserRequestParams,
} from '@post-office/cache-clients/helpers/tap-client/helper';

export type { TapTraitsRequestConfig };

export type GetTapTraitsForUserInput = {
	userId: string;
	tapProvider: SharedContractsTapProvider;
	logger?: HierarchicalLogger;
	config?: TapTraitsRequestConfig;
};

export type GetTapTraitsForSiteUserInput = GetTapTraitsForUserInput & {
	siteId: string;
};

export type GetTapTraitsForSiteInput = {
	siteId: string;
	tapProvider: SharedContractsTapProvider;
	logger?: HierarchicalLogger;
};

export type TapTraits = Record<string, string | boolean | number> | undefined;

export class TapProvider implements SharedContractsTapProvider {
	constructor(
		private readonly metrics: Metrics,
		private readonly logger: HierarchicalLogger,
		private readonly tapClient: CachedTapClient,
	) {}

	public async getTapTraits(
		resourceIds: string[],
		useLongCache: boolean = false,
		config?: TapTraitsRequestConfig,
	): Promise<Entity[] | undefined> {
		try {
			const request = {
				resourceIds,
				requester: 'post-office',
			};

			let tapResponse;
			if (useLongCache) {
				tapResponse = await this.tapClient.postForOptoutTraitsLongCache(request, undefined, config);
			} else {
				tapResponse = await this.tapClient.postForEntityTraits(request, undefined, config);
			}

			this.metrics
				.counter('post-office.tap-client', {
					status: 'success',
					cached: useLongCache.toString(),
					endPoint: 'postForEntityTraits',
					method: 'POST',
				})
				.incr();

			return tapResponse.data.entities;
		} catch (error) {
			this.metrics
				.counter('post-office.tap-client', {
					status: 'error',
					cached: useLongCache.toString(),
					endPoint: 'postForEntityTraits',
					method: 'POST',
				})
				.incr();

			this.logger.error({ error: error }, 'Error occurred when getting TAP traits');
			throw error;
		}
	}
	/*
	 * Gets all TAP traits (including audiences) for a SITE USER.
	 * This is cached for 5 minutes.
	 */
	public async getSiteUserTraits(
		siteUserRequestParams: SiteUserRequestParams,
	): Promise<Entity[] | undefined> {
		return getSiteUserTraitsHelper(siteUserRequestParams, {
			cachedTapClient: this.tapClient,
			requester: 'post-office',
			logger: this.logger,
		});
	}
}

/**
 * Shared utility to fetch TAP traits for a given entity
 * @param resourceAri - The resource ARI string of the entity to fetch TAP Traits for.
 * @param resourceType - The type of the resource (e.g. 'site-user', 'identity-user').
 * @param tapProvider - The TAP Provider to use.
 * @param logger - Optional logger for logging.
 * @returns The TAP Traits for the resource.
 */
const fetchTapTraits = async (
	resourceAri: string,
	resourceType: string,
	tapProvider: SharedContractsTapProvider,
	logger?: HierarchicalLogger,
	config?: TapTraitsRequestConfig,
): Promise<TapTraits> => {
	const useLongCache = false; // Long Cache option is for Opt-out with a 10hr TTL, default option is 5-min TTL
	try {
		const tapSidecarResponse = await tapProvider.getTapTraits([resourceAri], useLongCache, config);

		const tapTraits = tapSidecarResponse?.find(
			({ resourceId }) => resourceId === resourceAri,
		)?.attributes;
		logger?.info(
			{
				resourceType,
				tapTraitsLength: tapTraits !== undefined ? Object.keys(tapTraits).length : undefined,
			},
			`Successfully fetched TAP Traits for ${resourceType}`,
		);
		return tapTraits;
	} catch (error) {
		logger?.error({ error, resourceType }, `Error fetching TAP Traits for ${resourceType}`);
		throw error;
	}
};
/**
 * Fetches TAP Traits for a site-user from TAP Sidecar.
 * @param userId - The user ID to fetch TAP Traits for (Atlassian Account ID).
 * @param siteId - The site ID to fetch TAP Traits for.
 * @param tapProvider - The TAP Provider to use.
 * @param logger - Optional logger for logging.
 * @returns The TAP Traits for the site-user.
 */
export const getTapTraitsForSiteUser = async ({
	userId,
	siteId,
	tapProvider,
	logger,
	config,
}: GetTapTraitsForSiteUserInput): Promise<TapTraits> => {
	const siteUserResourceAri = MeasurementSiteUserAri.create({
		siteId,
		aaId: userId,
	}).toString();
	return fetchTapTraits(siteUserResourceAri, 'site-user', tapProvider, logger, config);
};

/**
 * Fetches TAP Traits for a site from TAP Sidecar.
 * @param siteId - The site ID to fetch TAP Traits for.
 * @param tapProvider - The TAP Provider to use.
 * @param logger - Optional logger for logging.
 * @returns The TAP Traits for the site.
 */
export const getTapTraitsForSite = async ({
	siteId,
	tapProvider,
	logger,
}: GetTapTraitsForSiteInput): Promise<TapTraits> => {
	const siteResourceAri = PlatformSiteAri.create({
		siteId,
	}).toString();
	return fetchTapTraits(siteResourceAri, 'site', tapProvider, logger);
};

/**
 * Fetches TAP Traits for an identity user from TAP Sidecar.
 * @param userId - The user ID to fetch TAP Traits for (Atlassian Account ID).
 * @param tapProvider - The TAP Provider to use.
 * @param logger - Optional logger for logging.
 * @returns The TAP Traits for the identity user.
 */
export const getTapTraitsForIdentityUser = async ({
	userId,
	tapProvider,
	logger,
}: GetTapTraitsForUserInput): Promise<TapTraits> => {
	const aaidResourceAri = IdentityUserAri.create({
		userId,
	}).toString();
	return fetchTapTraits(aaidResourceAri, 'identity-user', tapProvider, logger);
};
